import { useEffect, useRef, useState, startTransition } from "react";
import { Button } from "primereact/button";
import { Icon } from "@iconify/react";
import { InputTextCustom } from "medere-ui-framework";
import { useNavigate, useSearchParams } from "react-router";

import {countStudyOrdersByProfessional, generatePdf, getRecentStudyOrdersByProfessional, getStudyOrder, getStudyOrderErrorMessage, getStudyOrderPDF, getStudyOrders } from "../../services/StudyOrderController";

import { PatientRecordDTO, StudyOrderDTO } from "../../interface/interface";
import { getPatientData } from "../../services/digitalRPservice";
import { getPatientByHealthPatientServiceId } from "../../services/HealthPatientServiceController";
import type { StudyOrderFlowParams } from "./StudyOrderFlowParams";

import StudyOrderPrescriptionModal from "./StudyOrderPrescriptionModal";

import "./StudyOrderHistoryView.scss";

export type ResolvedStudyOrderContext = {
  orderId: number;
  patientId?: number;
  healthPatientServiceId?: number;
  professionalId?: number;
  patient?: PatientRecordDTO | null;
  detail?: any;
  pdfEncoded?: string;
  pdfFileId?: number;
  order?: StudyOrderDTO;
};

interface StudyOrderHistoryViewProps {
  patientId?: number;
  healthPatientServiceId?: number;
  professionalId?: number;
  studyType?: string;
  embedded?: boolean;
  onOpenOrderFlow?: (params?: StudyOrderFlowParams) => void;
  onBack?: () => void;
  modalOnly?: boolean;
  externalOpenOrder?: StudyOrderDTO | null;
  onExternalOpenOrderConsumed?: () => void;
}

const dedupeOrdersByStudyOrder = (rows: StudyOrderDTO[]): StudyOrderDTO[] => {
  const unique = new Map<string, StudyOrderDTO>();

  rows.forEach((row) => {
    const key = String(row?.studyorder ?? "");
    if (!key || unique.has(key)) return;
    unique.set(key, row);
  });

  return Array.from(unique.values());
};

const toPositiveNumber = (...values: any[]): number | undefined => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return undefined;
};

const getNestedValue = (source: any, path: string) =>
  path.split(".").reduce((current, key) => current?.[key], source);

const getFirstPositiveFromPaths = (source: any, paths: string[]): number | undefined => {
  for (const path of paths) {
    const resolved = toPositiveNumber(getNestedValue(source, path));
    if (resolved) return resolved;
  }

  return undefined;
};

const ORDER_EXPIRATION_DAYS = 60;
const ORDER_EXPIRATION_MS = ORDER_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

const normalizeSearchText = (value: unknown) => String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const retryAsync = async <T,>(task: () => Promise<T>, attempts: number, delayMs: number): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;

      if (attempt < attempts - 1) {
        await sleep(delayMs);
      }
    }
  }

  throw lastError;
};

const buildPatientFallback = (source: any, patientId?: number): PatientRecordDTO | null => {
  const resolvedPatientId = toPositiveNumber(
    patientId,
    source?.medereentity,
    source?.patientId,
    source?.patientid,
    source?.patient
  );

  if (!resolvedPatientId && !source?.fullname && !source?.patientfullname) {
    return null;
  }

  return {
    medereentitytype: Number(source?.medereentitytype ?? 0),
    medereentity: resolvedPatientId ?? 0,
    active: Boolean(source?.active ?? true),
    fullname: String(source?.fullname ?? source?.patientfullname ?? "").trim(),
    firstname: String(source?.firstname ?? "").trim(),
    lastname: String(source?.lastname ?? "").trim(),
    document: String(source?.document ?? "").trim(),
    documenttype: String(source?.documenttype ?? "").trim(),
    insurances: source?.insurances,
    insurancesToDelete: undefined,
    username: String(source?.username ?? ""),
    clinichistoryid: String(source?.clinichistoryid ?? ""),
    birthdate: source?.birthdate,
    gender: String(source?.gender ?? ""),
    address: String(source?.address ?? ""),
    mobilephone: String(source?.mobilephone ?? ""),
    email: String(source?.email ?? ""),
    entitytag: Number(source?.entitytag ?? 0),
    id: Number(source?.id ?? 0),
    isonlypatient: Boolean(source?.isonlypatient ?? false),
    typedesc: String(source?.typedesc ?? ""),
    medereentityuserid: String(source?.medereentityuserid ?? ""),
    tags: String(source?.tags ?? ""),
    tagsandcolors: String(source?.tagsandcolors ?? ""),
    fullpatientinfo: String(source?.fullpatientinfo ?? ""),
    observations: String(source?.observations ?? ""),
  };
};

const isConsumedOrder = (order?: StudyOrderDTO | null) =>
  order?.statusFilterKey === "CONSUMED" ||
  String(order?.statusLabel ?? "").toLowerCase().includes("consum");

const isExpiredOrder = (order?: StudyOrderDTO | null, referenceDate = new Date()) => {
  if (!order) return false;
  if (isConsumedOrder(order)) return false;
  if (order.statusFilterKey === "EXPIRED") return true;
  if (String(order.statusLabel ?? "").toLowerCase().includes("venc")) return true;

  const orderDate = new Date(order.orderdate);
  if (Number.isNaN(orderDate.getTime())) return false;

  return referenceDate.getTime() >= orderDate.getTime() + ORDER_EXPIRATION_MS;
};

const normalizeStudyOrderStatus = (order: StudyOrderDTO, referenceDate = new Date()): StudyOrderDTO => {
  if (isConsumedOrder(order)) return order;
  if (!isExpiredOrder(order, referenceDate)) return order;

  return {
    ...order,
    statusFilterKey: "EXPIRED",
    statusLabel: order.statusLabel && order.statusLabel.toLowerCase().includes("venc") ? order.statusLabel : "Vencida"
  };
};

export default function StudyOrderHistoryView({
  patientId: patientIdProp,
  healthPatientServiceId: healthPatientServiceIdProp,
  professionalId: professionalIdProp,
  studyType: studyTypeProp,
  embedded = false,
  onOpenOrderFlow,
  onBack,
  modalOnly = false,
  externalOpenOrder,
  onExternalOpenOrderConsumed,
}: StudyOrderHistoryViewProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const patientIdFromQuery = Number(searchParams.get("medereentityID"));

  const hpsIdFromQuery = searchParams.get("healthPatientServiceId") ? Number(searchParams.get("healthPatientServiceId")) : undefined;

  const professionalIdFromQuery = searchParams.get("professionalId") ? Number(searchParams.get("professionalId")) : 0;

  const studyTypeFromQuery = searchParams.get("studyType") || undefined;
  const scopeToPatientOnly = searchParams.get("scopeToPatient") === "true";

  const patientId = patientIdProp ?? patientIdFromQuery;
  const hpsId = healthPatientServiceIdProp ?? hpsIdFromQuery;
  const professionalIdNum = professionalIdProp ?? professionalIdFromQuery;
  const studyType = studyTypeProp ?? studyTypeFromQuery;
  const useProfessionalListing = professionalIdNum > 0 && !scopeToPatientOnly;

  const [orders, setOrders] = useState<StudyOrderDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [totalOrders, setTotalOrders] = useState(0);
  const [offset, setOffset] = useState(0);
  const [activeOrderId, setActiveOrderId] = useState<number | null>(null);

  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [orderModalLoading, setOrderModalLoading] = useState(false);
  const [selectedOrderContext, setSelectedOrderContext] =
    useState<ResolvedStudyOrderContext | null>(null);

  const pageSize = 50;

  const cardsContainerRef = useRef<HTMLDivElement | null>(null);
  const orderContextCacheRef = useRef<Record<number, ResolvedStudyOrderContext>>({});
  const openOrderRequestIdRef = useRef(0);
  const onExternalOpenOrderConsumedRef = useRef(onExternalOpenOrderConsumed);

  useEffect(() => {
    onExternalOpenOrderConsumedRef.current = onExternalOpenOrderConsumed;
  }, [onExternalOpenOrderConsumed]);

  const invalidateOpenOrderRequest = () => {
    openOrderRequestIdRef.current += 1;
    setOrderModalLoading(false);
    setActiveOrderId(null);
  };

  useEffect(() => {
    if (modalOnly) {
      setLoading(false);
      setOrders([]);
      setOffset(0);
      setTotalOrders(0);
      setLoadError(null);
      return;
    }

    setLoading(true);
    setOrders([]);
    setOffset(0);
    setTotalOrders(0);
    setLoadError(null);

    if (useProfessionalListing) {
      Promise.all([
        getRecentStudyOrdersByProfessional(professionalIdNum, pageSize, 0),
        countStudyOrdersByProfessional(professionalIdNum),
      ])
      .then(([recent, total]) => {
          const deduped = dedupeOrdersByStudyOrder(recent ?? []).map((order) =>
            normalizeStudyOrderStatus(order)
          );
          setOrders(deduped);
          setTotalOrders(total);
          setOffset(deduped.length);
        }).catch(() => {
          setLoadError("No se pudo cargar el historial de órdenes.");
        }).finally(() => setLoading(false));

      return;
    }

    if (!patientId) {
      setOrders([]);
      setLoading(false);
      return;
    }

    getStudyOrders(patientId, scopeToPatientOnly ? undefined : hpsId)
      .then((data) => {
        let deduped = dedupeOrdersByStudyOrder(data ?? []).map((order) => normalizeStudyOrderStatus(order));

        if (scopeToPatientOnly) {
          deduped = deduped.filter((order) => !isExpiredOrder(order));
        }

        setOrders(deduped);
        setTotalOrders(deduped.length);
      }).catch(() => {
        setLoadError("No se pudo cargar el historial de órdenes.");
      }).finally(() => setLoading(false));
  }, [modalOnly, patientId, hpsId, professionalIdNum, useProfessionalListing, scopeToPatientOnly]);

  const handleLoadMore = () => {
    if (!useProfessionalListing || loadingMore || offset >= totalOrders) return;

    setLoadingMore(true);

    getRecentStudyOrdersByProfessional(professionalIdNum, pageSize, offset).then((next) => {
      setOrders((prev) => {
        const merged = dedupeOrdersByStudyOrder([
          ...(prev ?? []),
          ...(next ?? []),
        ]).map((order) => normalizeStudyOrderStatus(order));

        setOffset(merged.length);

        return merged;
      });
    }).finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    if (!useProfessionalListing) return;

    const container = cardsContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (loadingMore || loading || offset >= totalOrders) return;

      const threshold = 120;
      const reachedBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;

      if (reachedBottom) {
        handleLoadMore();
      }
    };

    container.addEventListener("scroll", onScroll);

    return () => container.removeEventListener("scroll", onScroll);
  }, [useProfessionalListing, loadingMore, loading, offset, totalOrders]);

  const filteredOrders = orders.filter((order) => {
    if (!search) return true;

    const haystack = normalizeSearchText(
      [
        order.studyorder,
        order.patientfullname,
        order.patientid,
        order.icondescription,
        order.presumptivediagnostic,
        order.code,
        order.studyTypeDescription,
        order.statusLabel,
      ].join(" ")
    );

    return haystack.includes(normalizeSearchText(search));
  }).sort((a, b) => new Date(b.orderdate).getTime() - new Date(a.orderdate).getTime());

  const buildFlowQuery = (params?: StudyOrderFlowParams) => {
    const qs = new URLSearchParams();

    if (params?.patientId) {
      qs.set("medereentityID", String(params.patientId));
    }

    if (params?.professionalId) {
      qs.set("professionalId", String(params.professionalId));
    }

    if (params?.healthPatientServiceId) {
      qs.set("healthPatientServiceId", String(params.healthPatientServiceId));
    }

    if (params?.studyType) {
      qs.set("studyType", params.studyType);
    }

    if (params?.mode) {
      qs.set("mode", params.mode);
    }

    if (params?.copyFromHpsId) {
      qs.set("copyFromHpsId", String(params.copyFromHpsId));
    }

    if (params?.scopeToPatientOnly) {
      qs.set("scopeToPatient", "true");
    }

    return qs;
  };

  const resolveOrderContext = async (order: StudyOrderDTO): Promise<ResolvedStudyOrderContext> => {
    const cached = orderContextCacheRef.current[order.studyorder];

    if (cached?.pdfEncoded && cached?.patient) {
      return cached;
    }

    const orderHealthPatientServiceId = toPositiveNumber(order.studyorder,(order as any)?.parenthps);

    let detail = cached?.detail;

    if (!detail || (!cached?.patient && professionalIdNum <= 0)) {
      try {
        detail = await getStudyOrder(order.studyorder);
      } catch {
        detail = cached?.detail;
      }
    }

    const resolvedPatientId = toPositiveNumber(
      patientId,
      order.patientid,
      getFirstPositiveFromPaths(detail, [
        "patientId",
        "patientid",
        "patient.medereentity",
        "patient.medereEntity",
        "patient.patientid",
        "patient.patientId",
        "medereentity",
        "medereEntity",
        "patient",
      ])
    );

    const resolvedHealthPatientServiceId = toPositiveNumber(
      orderHealthPatientServiceId,
      hpsId,
      getFirstPositiveFromPaths(detail, [
        "healthPatientServiceId",
        "healthPatientServiceID",
        "healthpatientserviceid",
        "healthPatientService.healthPatientService",
        "healthPatientService.healthpatientservice",
        "healthpatientservice.healthPatientService",
        "healthpatientservice.healthpatientservice",
        "healthpatientservice",
        "healthPatientService",
      ])
    );

    const resolvedProfessionalId = toPositiveNumber(
      getFirstPositiveFromPaths(detail, [
        "professionalId",
        "professionalid",
        "professional.professionaid",
        "professional.professionaId",
        "professional.medereentity",
        "professional.medereEntity",
        "professional",
      ]),
      professionalIdNum
    );

    let resolvedPatient: PatientRecordDTO | null = cached?.patient ?? null;

    if (!resolvedPatient) {
      if (resolvedPatientId) {
        try {
          resolvedPatient = await retryAsync(
            () => getPatientData(resolvedPatientId),
            3,
            300
          );
        } catch {
          resolvedPatient = null;
        }
      }

      if (!resolvedPatient && resolvedHealthPatientServiceId) {
        try {
          resolvedPatient = await retryAsync(
            () => getPatientByHealthPatientServiceId(resolvedHealthPatientServiceId),
            3,
            300
          );
        } catch {
          resolvedPatient = null;
        }
      }

      if (!resolvedPatient) {
        resolvedPatient =
          buildPatientFallback(order, resolvedPatientId) ??
          buildPatientFallback(detail?.patient, resolvedPatientId) ??
          buildPatientFallback(detail, resolvedPatientId);
      }
    }

    let pdfEncoded = cached?.pdfEncoded;
    let pdfFileId = cached?.pdfFileId;

    if (!pdfEncoded || !pdfFileId) {
      await generatePdf(order.studyorder);

      for (
        let attempt = 0;
        attempt < 8 && (!pdfEncoded || !pdfFileId);
        attempt += 1
      ) {
        const file = await getStudyOrderPDF(order.studyorder);

        pdfEncoded = file?.encodedPdf;
        pdfFileId = file?.id;

        if (pdfEncoded && pdfFileId) break;

        if (attempt < 7) {
          await sleep(500);
        }
      }
    }

    const context: ResolvedStudyOrderContext = {
      orderId: order.studyorder,
      patientId: resolvedPatientId,
      healthPatientServiceId: resolvedHealthPatientServiceId ?? orderHealthPatientServiceId,
      professionalId: resolvedProfessionalId,
      patient: resolvedPatient,
      detail,
      pdfEncoded,
      pdfFileId,
      order: normalizeStudyOrderStatus(order),
    };

    orderContextCacheRef.current[order.studyorder] = context;

    return context;
  };

  const handleNewOrder = () => {
    const params: StudyOrderFlowParams = {
      patientId,
      healthPatientServiceId: hpsId,
      professionalId: professionalIdNum > 0 ? professionalIdNum : undefined,
      studyType,
      scopeToPatientOnly: scopeToPatientOnly || undefined,
    };

    if (onOpenOrderFlow) {
      onOpenOrderFlow(params);
      return;
    }

    startTransition(() => {
      const qs = buildFlowQuery(params);
      navigate(`/studyorderview?${qs.toString()}`);
    });
  };

  const handleRepeatOrder = async (
    context?: ResolvedStudyOrderContext | null
  ) => {
    try {
      if (!context) return;

      const copyFromHpsId = context.healthPatientServiceId ?? context.orderId;

      if (!copyFromHpsId) {
        setActionError("No se pudo obtener el servicio origen para repetir esta orden.");
        return;
      }

      const params: StudyOrderFlowParams = {
        patientId: context.patientId,
        healthPatientServiceId: copyFromHpsId,
        professionalId: context.professionalId,
        studyType,
        mode: "COPY_STUDY_ORDER",
        copyFromHpsId,
        scopeToPatientOnly: scopeToPatientOnly || undefined,
      };

      setSelectedOrderContext(null);

      if (onOpenOrderFlow) {
        onOpenOrderFlow(params);
        return;
      }

      startTransition(() => {
        const qs = buildFlowQuery(params);
        navigate(`/studyorderview?${qs.toString()}`);
      });
    } catch (error) {
      setActionError(getStudyOrderErrorMessage(error, "No se pudo preparar la repetición de la orden."));
    }
  };

  const handleOpenOrderModal = async (order: StudyOrderDTO) => {
    const requestId = ++openOrderRequestIdRef.current;

    try {
      setActionError(null);
      setActiveOrderId(order.studyorder);
      setOrderModalVisible(true);
      setOrderModalLoading(true);

      let context = await resolveOrderContext(order);

      if (requestId !== openOrderRequestIdRef.current) return;

      if (!context.pdfEncoded) {
        await sleep(600);
        context = await resolveOrderContext(order);

        if (requestId !== openOrderRequestIdRef.current) return;
      }

      if (!context.patient || !context.professionalId || !context.pdfEncoded) {
        setActionError("No se pudo obtener la información completa de la orden.");
      } else {
        setActionError(null);
      }

      setSelectedOrderContext(context);
    } catch (error) {
      if (requestId !== openOrderRequestIdRef.current) return;

      setActionError(
        getStudyOrderErrorMessage(
          error,
          "No se pudo abrir la orden seleccionada."
        )
      );

      setSelectedOrderContext(null);
    } finally {
      if (requestId !== openOrderRequestIdRef.current) return;

      setOrderModalLoading(false);
      setActiveOrderId(null);
    }
  };

  const handleCloseModal = () => {
    setOrderModalVisible(false);
    invalidateOpenOrderRequest();
    setSelectedOrderContext(null);
    setActionError(null);
    onExternalOpenOrderConsumedRef.current?.();
  };

  useEffect(() => {
    if (!externalOpenOrder) return;

    handleOpenOrderModal(externalOpenOrder);
  }, [externalOpenOrder?.studyorder]);

  useEffect(() => {
    return () => {
      invalidateOpenOrderRequest();
      onExternalOpenOrderConsumedRef.current?.();
    };
  }, []);

  const canRepeatSelectedOrder = isExpiredOrder(selectedOrderContext?.order);

  if (modalOnly) {
    return (
      <StudyOrderPrescriptionModal
        visible={orderModalVisible}
        loading={orderModalLoading}
        context={selectedOrderContext}
        error={actionError}
        onPdfError={(error) => setActionError(`No se pudo mostrar el PDF: ${error.message}`)}
        expired={canRepeatSelectedOrder}
        onClose={handleCloseModal}
        onRepeat={() => handleRepeatOrder(selectedOrderContext)}
        onPatientUpdated={(patientUpdated: PatientRecordDTO) => {
          setSelectedOrderContext((prev) =>
            prev ? { ...prev, patient: patientUpdated } : prev
          );
        }}
      />
    );
  }

  return (
    <div className={`recipe-history-view study-order-history-view${embedded ? " embedded" : ""}`}>
      <div>
        <div className="study-order-history-content">
          <div className="study-order-history-header">
            <h1>Historial de órdenes de estudio</h1>
          </div>

          <div className="study-order-history-toolbar">
            <div className="patient-search-container study-order-search">
              <InputTextCustom
                className={`patient-input ${search ? "active" : ""} border-grey`}
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                placeholder="Buscar paciente, orden o estudio"
              />
              <i className="pi pi-search patient-search-icon" />
            </div>

            <Button
              className="btnRpContinue study-order-new-button"
              icon={<i className="pi pi-file" />}
              label="NUEVA ORDEN"
              onClick={handleNewOrder}
            />
          </div>

          {!loading && !loadError && totalOrders > 0 && (
            <div className="study-order-history-summary">
              Mostrando {orders.length} de {totalOrders} órdenes
            </div>
          )}

          {loading && (
            <div className="study-order-history-message">
              Cargando órdenes...
            </div>
          )}

          {!loading && actionError && (
            <div className="study-order-history-message">
              {actionError}
            </div>
          )}

          {!loading && loadError && (
            <div className="study-order-history-message">
              {loadError}
            </div>
          )}

          {!loading && !loadError && filteredOrders.length === 0 && (
            <div className="study-order-history-message">
              No hay órdenes de estudio para mostrar.
            </div>
          )}

          <div
            className="study-order-history-cards"
            ref={cardsContainerRef}
          >
            {filteredOrders.map((order) => (
              <button
                key={order.studyorder}
                type="button"
                className="study-order-history-card"
                onClick={() => handleOpenOrderModal(order)}
                disabled={activeOrderId === order.studyorder}
              >
                <div className="study-order-card-header">
                  <div className="study-order-card-title-area">
                    <span className="study-order-card-title">
                      {order.patientfullname || "Paciente sin nombre"}
                    </span>

                    {order.statusLabel && (
                      <span className="study-order-card-status">
                        {order.statusLabel}
                      </span>
                    )}
                  </div>

                  <Icon
                    icon="mdi:chevron-right"
                    className="study-order-card-arrow"
                  />
                </div>

                <div className="study-order-card-subtitle">
                  Orden #{order.studyorder}
                </div>

                <div className="study-order-card-subtitle">
                  {order.icondescription} •{" "}
                  {new Date(order.orderdate).toLocaleDateString("es-AR")}
                </div>

                {order.presumptivediagnostic && (
                  <div className="study-order-card-description">
                    {order.presumptivediagnostic}
                  </div>
                )}
              </button>
            ))}

            {useProfessionalListing && loadingMore && (
              <div className="study-order-history-message">
                Cargando más órdenes...
              </div>
            )}
          </div>
        </div>

        <div className="study-order-history-mobile-footer">
          <Button
            className="btnRpContinue study-order-mobile-new-button"
            label="NUEVA ORDEN"
            onClick={handleNewOrder}
          />

          {onBack && (
            <Button
              label="Volver"
              type="button"
              className="study-order-mobile-back-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                invalidateOpenOrderRequest();
                onExternalOpenOrderConsumedRef.current?.();
                onBack();
              }}
            />
          )}
        </div>
      </div>

      <StudyOrderPrescriptionModal
        visible={orderModalVisible}
        loading={orderModalLoading}
        context={selectedOrderContext}
        error={actionError}
        onPdfError={(error) => setActionError(`No se pudo mostrar el PDF: ${error.message}`)}
        expired={canRepeatSelectedOrder}
        onClose={handleCloseModal}
        onRepeat={() => handleRepeatOrder(selectedOrderContext)}
        onPatientUpdated={(patientUpdated: PatientRecordDTO) => {
          setSelectedOrderContext((prev) =>
            prev ? { ...prev, patient: patientUpdated } : prev
          );
        }}
      />
    </div>
  );
}

